import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import PasswordInput from '../../components/common/PasswordInput';
import '../../assets/styles/auth.css';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast.success(`Welcome back, ${result.user.firstName}!`);
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Form Section */}
        <div className="auth-form-section">
          <Link to="/" className="auth-logo">
            <i className="fas fa-shield-alt"></i>
            <span>SafeWork<span className="brand-360">360</span></span>
          </Link>

          <div className="auth-header">
            <h1>Sign in</h1>
            <p>Enter your credentials to access your account</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <div className="input-icon-wrapper">
                <i className="fas fa-envelope input-icon"></i>
                <input
                  type="email"
                  id="email"
                  className="form-control"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <PasswordInput
                leftIcon="fas fa-lock"
                wrapperClassName="input-icon-wrapper"
                className="form-control"
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="form-row-between">
              <div className="form-check-custom">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="remember">Remember me</label>
              </div>
              <Link to="/forgot-password" className="forgot-link">
                Forgot password?
              </Link>
            </div>

            <button type="submit" className="btn-auth-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm"></span>
                  Signing in...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i>
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="auth-footer">
            Don't have an account? <Link to="/register">Register your organisation</Link>
          </div>
        </div>

        {/* Features Section */}
        <div className="auth-features-section">
          <div className="features-content">
            <h2>Manage compliance with confidence</h2>
            <p>Track environmental and OHS requirements across all your projects with real-time monitoring and reporting.</p>
            
            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-project-diagram"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Project Management</h4>
                  <p>Track EIA conditions and monitor compliance across all projects</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-clipboard-check"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Inspections & Monitoring</h4>
                  <p>Schedule inspections and collect evidence with geo-tagging</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Real-time Analytics</h4>
                  <p>Gain insights with powerful compliance dashboards</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-users"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Team Collaboration</h4>
                  <p>Assign tasks and manage your team efficiently</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
